import React from 'react';
import LegalPageLayout, { LegalSection } from './LegalPageLayout';

const sections: LegalSection[] = [
  {
    title: '1. Purpose of this policy',
    body: [
      'This Privacy Policy explains how Dinelyt collects, uses, stores, and protects personal and business information when restaurants use our platform and when diners interact with Dinelyt-powered ordering and service experiences.',
      'Our goal is to keep data practices understandable and proportionate to the kind of hospitality workflows Dinelyt supports, including onboarding, menu access, dine-in sessions, staff operations, billing, and support.',
    ],
  },
  {
    title: '2. Information we may collect',
    body: [
      'We may collect business and account information such as restaurant name, owner or manager name, email address, phone number, restaurant address, operational details, uploaded brand assets, tax or regulatory documents, and role-based account information needed to set up and manage the service.',
      'We may also collect diner-facing information used within the ordering journey, such as name, contact details, table identifier, restaurant session details, order information, billing data, and support-related communications where relevant to the dining experience.',
    ],
  },
  {
    title: '3. How we use information',
    body: [
      'Dinelyt uses collected information to operate the platform, enable restaurant onboarding, support QR ordering sessions, process menu and order workflows, facilitate billing and invoice experiences, manage staff access, provide analytics, respond to support requests, and improve product reliability and usability.',
      'We may also use information to communicate with restaurants about their account, onboarding progress, service updates, policy updates, and support requests.',
    ],
  },
  {
    title: '4. Session, device and operational data',
    body: [
      'When the application is used, we may process technical and operational information such as login state, role-based access details, token-based session information, table session identifiers, browser metadata, and service logs. This helps us keep the platform functional, secure, and traceable for support and operational troubleshooting.',
      'We may also use limited usage information to understand platform performance, diagnose issues, and improve key workflows across restaurant and diner experiences.',
    ],
  },
  {
    title: '5. Sharing of information',
    body: [
      'We do not sell personal information. We may share data with service providers and infrastructure partners that help us operate Dinelyt, such as hosting, storage, communication, analytics, or support-related vendors, but only to the extent reasonably necessary to provide the service.',
      'Information submitted within a restaurant workflow may also be visible to that restaurant and its authorized staff when needed to fulfill orders, manage tables, prepare bills, or resolve customer issues.',
    ],
  },
  {
    title: '6. Storage and retention',
    body: [
      'We retain information for as long as reasonably necessary to provide the service, maintain business records, resolve disputes, support restaurant operations, comply with legal requirements, and enforce our agreements. Retention periods may vary depending on the type of information and the relationship involved.',
      'Uploaded files, operational records, and account-related data may be retained for continuity, auditability, and support unless deletion is required by law or agreed business process.',
    ],
  },
  {
    title: '7. Security',
    body: [
      'Dinelyt uses reasonable technical and organizational measures intended to protect information against unauthorized access, misuse, alteration, and loss. These measures may include authenticated access, role-based permissions, managed storage, and operational logging.',
      'No internet-based system can be guaranteed to be fully secure. For that reason, restaurant partners should protect credentials carefully and limit access to authorized staff only.',
    ],
  },
  {
    title: '8. Cookies and similar technologies',
    body: [
      'Our website and application may use browser storage, cookies, or similar mechanisms to maintain session continuity, remember preferences, improve usability, and support basic platform functionality. Some features may not work correctly if these are disabled.',
      'We do not rely on these technologies to create intrusive profiles; they are primarily used to deliver a smoother and more reliable product experience.',
    ],
  },
  {
    title: '9. Your choices and rights',
    body: [
      'Depending on the nature of your relationship with Dinelyt and applicable law, you may request access to, correction of, or deletion of certain information. Restaurant partners may also request updates to business account details and operational information maintained on the platform.',
      'Where a restaurant controls diner-facing data entered through its own workflows, that restaurant may also be involved in handling requests connected with specific orders or dining sessions.',
    ],
  },
  {
    title: '10. Third-party services and links',
    body: [
      'Dinelyt may integrate with or reference third-party services such as communication providers, payment channels, storage infrastructure, or external links. Their handling of information is governed by their own policies and terms.',
      'We encourage users to review the privacy and security practices of any third-party service they access in connection with the platform.',
    ],
  },
  {
    title: '11. Children’s privacy',
    body: [
      'Dinelyt is intended for business and dining use and is not designed as a service directed specifically to children. If we become aware that information has been submitted in a way that requires special handling under applicable law, we will take reasonable steps to address it.',
    ],
  },
  {
    title: '12. Policy updates and contact',
    body: [
      'We may update this Privacy Policy from time to time to reflect product, legal, or operational changes. The updated version will be posted on our website with a revised effective date.',
      'If you have privacy-related questions, concerns, or requests, contact us at info@dinelyt.com and include enough detail for us to identify the relevant restaurant, account, or interaction.',
    ],
  },
];

const PrivacyPolicyPage: React.FC = () => (
  <LegalPageLayout
    eyebrow="Privacy Notice"
    title="Privacy Policy"
    intro="This policy describes how Dinelyt handles information connected with restaurant onboarding, staff operations, QR-based dine-in journeys, and customer interactions across the Dinelyt platform. It is written to reflect the way a modern hospitality operations product is actually used."
    effectiveDate="April 19, 2026"
    sections={sections}
  />
);

export default PrivacyPolicyPage;
